import { ColumnDef } from "@tanstack/react-table"
import { Button } from '@/components/ui/button';
import { DataTable, SortableHeader } from '@/components/ui/data-table';
import { Phone } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { CountryRate } from '@/types/pricing';
import { useCountriesData } from '@/hooks/usePricing';
import { useCurrency } from '@/hooks/useCurrency';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { PricingCountryCard } from '@/components/PricingCountryCard';
import { HeroStats } from '@/components/StatsGrid';
import { H1, H3, Lead } from '@/components/ui/typography';
import { createFileRoute, Link } from '@tanstack/react-router'
import { countryToSlug } from '@/lib/countries'
import { useLang } from '@/hooks/useLang'
import { Flag } from '@/components/Flag'

export const Route = createFileRoute('/$lang/pricing')({
  staticData: {
    title: 'Pricing — PhoneSalad',
    description: 'See call rates to 200+ countries. Pay only for what you use with no monthly fees, no contracts, and no hidden charges. Compare landline and mobile rates.',
    sitemap: { priority: '0.9', changefreq: 'weekly' },
  },
  component: PricingPage,
})

function PricingPage() {
  const { t } = useTranslation(['marketing', 'common']);
  const { formatCurrency } = useCurrency();
  const lang = useLang()

  const {
    countries,
    popularCountries,
    stats,
    isLoading,
    isError,
    error
  } = useCountriesData();

  const columns: ColumnDef<CountryRate>[] = [
    {
      accessorKey: "country",
      header: ({ column }) => <SortableHeader column={column} label={t('pricing.country')} />,
      cell: ({ row }) => {
        const country = row.original;
        return (
          <div className="flex items-center gap-3">
            <Flag countryCode={country.countryCode} size="lg" />
            <div>
              <div className="font-medium flex items-center gap-2">
                {t(`countries.${country.countryCode}`)}
                {country.popular && (
                  <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                    {t('common.popular')}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "region",
      header: ({ column }) => <SortableHeader column={column} label={t('pricing.region')} />,
      cell: ({ row }) => (
        <span className="text-muted-foreground">{row.getValue("region")}</span>
      ),
    },
    {
      accessorKey: "landlineRate",
      header: ({ column }) => <SortableHeader column={column} label={`${t('pricing.landlineRate')} (${t('pricing.perMin')})`} />,
      cell: ({ row }) => {
        const amount = parseFloat(row.getValue("landlineRate"))
        const formatted = formatCurrency(amount, { useIntl: true })

        return <span className="font-mono font-semibold text-green-600">{formatted}</span>
      },
    },
    {
      accessorKey: "mobileRate",
      header: ({ column }) => <SortableHeader column={column} label={`${t('pricing.mobileRate')} (${t('pricing.perMin')})`} />,
      cell: ({ row }) => {
        const amount = parseFloat(row.getValue("mobileRate"))
        const formatted = formatCurrency(amount, { useIntl: true })

        return <span className="font-mono font-semibold text-green-600">{formatted}</span>
      }
    },
    {
      id: "actions",
      header: t('pricing.actions'),
      cell: ({ row }) => {
        const country = row.original

        return (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" asChild>
              <a href={`/dialer?phone=${encodeURIComponent(country.dialingCode)}`}>
                {t('pricing.callNow')}
              </a>
            </Button>
            <Button size="sm" variant="ghost" asChild>
              <Link to="/$lang/rates/$country" params={{ lang, country: countryToSlug(country.countryCode) }}>
                {t('common.details')}
              </Link>
            </Button>
          </div>
        )
      },
    },
  ];

  const renderMobileCard = (country: CountryRate) => {
    const landlineFormatted = formatCurrency(parseFloat(String(country.landlineRate)), { useIntl: true })
    const mobileFormatted = formatCurrency(parseFloat(String(country.mobileRate)), { useIntl: true })

    return (
      <Link
        to="/$lang/rates/$country"
        params={{ lang, country: countryToSlug(country.countryCode) }}
        className="flex items-center gap-3 px-4 py-3.5 hover:bg-muted/50 transition-colors"
      >
        {/* Flag */}
        <div className="shrink-0">
          <Flag countryCode={country.countryCode} size="lg" />
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          {/* Country name + popular badge */}
          <div className="flex items-center gap-2 text-sm font-medium">
            {t(`countries.${country.countryCode}`)}
            {country.popular && (
              <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                {t('common.popular')}
              </span>
            )}
          </div>

          {/* Region */}
          <div className="mt-0.5 text-xs text-muted-foreground">{country.region}</div>

          {/* Rates */}
          <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
            <span>
              {t('pricing.landlineRate')}:{' '}
              <span className="font-mono font-semibold text-green-600">{landlineFormatted}</span>
            </span>
            <span>
              {t('pricing.mobileRate')}:{' '}
              <span className="font-mono font-semibold text-green-600">{mobileFormatted}</span>
            </span>
          </div>
        </div>

        {/* Call Now button — stops propagation so it doesn't navigate to details */}
        <Button
          size="sm"
          variant="outline"
          className="shrink-0"
          asChild
          onClick={(e) => e.stopPropagation()}
        >
          <a href={`/dialer?phone=${encodeURIComponent(country.dialingCode)}`}>
            <Phone className="h-3.5 w-3.5" />
          </a>
        </Button>
      </Link>
    )
  }

  useEffect(() => {
    if (isError) {
      toast.error(error instanceof Error ? error.message : t('errors.generic'))
    }
  }, [isError, error, t])

  return (
    <>
        {/* Hero Section */}
        <section className="py-16 px-4 bg-gradient-to-b from-background to-muted/30">
          <div className="container mx-auto">
            <div className="max-w-4xl mx-auto text-center">
              <H1 className="mb-6">
                {t('pricing.title')}
              </H1>
              <Lead className="mb-8 max-w-2xl mx-auto">
                {t('pricing.description', { count: Math.floor((stats?.totalCountries ?? 0) / 10) * 10 })}
              </Lead>

              <HeroStats
                totalCountries={stats?.totalCountries}
                minRate={stats?.minRate}
              />
            </div>
          </div>
        </section>        {/* Pricing Table Section */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            {/* Popular Countries */}
            <div className="mb-8">
              <H3 className="mb-4 flex items-center gap-2">
                <span className="text-primary">⭐</span> {t('pricing.popularDestinations')}
              </H3>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {isLoading
                  ? Array.from({ length: 6 }).map((_, i) => (
                      <Skeleton key={i} className="aspect-[4/3] rounded-xl" />
                    ))
                  : popularCountries.slice(0, 6).map((country) => (
                      <PricingCountryCard key={country.countryCode} country={country} />
                    ))
                }
              </div>
            </div>

            {/* Interactive Data Table */}
            <div className="mb-8">
              <H3 className="mb-4">{t('pricing.allCountries', { count: countries.length })}</H3>
              {isLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-4 px-2 py-2">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-4 w-20" />
                    </div>
                  ))}
                </div>
              ) : (
                <DataTable columns={columns} data={countries} renderMobileCard={renderMobileCard} />
              )}
              <p className="text-xs text-muted-foreground mt-3">
                {t('pricing.surchargeDisclaimer')}
              </p>
            </div>
          </div>
        </section>

        {/* Information Section */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="container mx-auto max-w-4xl">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <H3 className="mb-4">{t('pricing.howItWorks.title')}</H3>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    {t('pricing.howItWorks.payAsYouGo')}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    {t('pricing.landlineMobileInfo')}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    {t('pricing.howItWorks.creditsNeverExpire')}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    {t('pricing.howItWorks.ratesIncludeAll')}
                  </li>
                </ul>
              </div>

              <div>
                <H3 className="mb-4">{t('pricing.needHelp.title')}</H3>
                <div className="space-y-4">
                  <p className="text-muted-foreground">
                    {t('pricing.needHelp.description')}
                  </p>
                  <div className="flex gap-3">
                    <Button variant="outline" asChild>
                      <a href="mailto:support@phonesalad.com">{t('pricing.needHelp.contactSupport')}</a>
                    </Button>
                    <Button asChild>
                      <a href="/dialer">{t('pricing.needHelp.getStarted')}</a>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </>
  )
}
